/**
 * The options a select field offers, and the colours they are shown in.
 *
 * A select option is stored as a name, a colour *name* and an id. The colour is
 * a name rather than a value because it is data that outlives any palette: a
 * row written years ago says `dark-blue`, and what that looks like is this
 * file's business, not the database's.
 *
 * The forty-two names and their values are upstream's, unchanged. The rewrite
 * re-expresses the design decisions in Tailwind; it does not re-decide them, and
 * these in particular are stored values that would change colour under everybody
 * if they were re-picked.
 */
import type { SelectOption } from '@/api/types'

/**
 * The palette, in the order it is offered: ten hues, four shades, then the two
 * upstream added afterwards and left at the end.
 */
export const SELECT_COLORS = {
  'light-blue': '#e7f4ff',
  'light-green': '#e0f9ed',
  'light-cyan': '#cff5fa',
  'light-orange': '#fdf5df',
  'light-yellow': '#fdf5df',
  'light-red': '#ffedec',
  'light-brown': '#f5e6dc',
  'light-purple': '#f9f1fd',
  'light-pink': '#f7e1f2',
  'light-gray': '#f4f4f0',
  blue: '#cee8fe',
  green: '#c0f2db',
  cyan: '#a0ebf5',
  orange: '#f9eac6',
  yellow: '#fde1a7',
  red: '#ffdbd9',
  brown: '#f5ceb0',
  purple: '#dfb9f7',
  pink: '#f7b2e7',
  gray: '#d5d5cc',
  'dark-blue': '#a0cdf2',
  'dark-green': '#a4f2cf',
  'dark-cyan': '#70e0ef',
  'dark-orange': '#fde1a7',
  'dark-yellow': '#fcd58d',
  'dark-red': '#f6c2bf',
  'dark-brown': '#f5c098',
  'dark-purple': '#cf96f2',
  'dark-pink': '#f285d9',
  'dark-gray': '#cbcbc0',
  'darker-blue': '#398dc8',
  'darker-green': '#45c992',
  'darker-cyan': '#41d6ea',
  'darker-orange': '#fac871',
  'darker-yellow': '#fac871',
  'darker-red': '#da827f',
  'darker-brown': '#f5a96e',
  'darker-purple': '#bf73ee',
  'darker-pink': '#ff6dde',
  'darker-gray': '#b7b7a8',
  'deep-dark-green': '#00a06b',
  'deep-dark-orange': '#f6bc5d',
} as const

export type SelectColor = keyof typeof SELECT_COLORS

export const SELECT_COLOR_NAMES = Object.keys(SELECT_COLORS) as SelectColor[]

/** What an option whose colour this build has never heard of is shown in. */
const UNKNOWN_COLOR = '#e5e5e5'

function isSelectColor(name: unknown): name is SelectColor {
  return typeof name === 'string' && name in SELECT_COLORS
}

/**
 * How dark a colour is, on the sRGB relative-luminance scale.
 *
 * Worked out rather than written down beside each colour, so that adding one to
 * the palette cannot leave it with unreadable text on it. The palette runs from
 * near-white to a mid green, which is exactly the range where guessing wrong is
 * easy.
 */
function luminance(hex: string): number {
  const channel = (from: number): number => {
    const value = Number.parseInt(hex.slice(from, from + 2), 16) / 255
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5)
}

/** The background and the text to put on it, for one stored colour name. */
export function selectColorStyle(color: unknown): {
  backgroundColor: string
  color: string
} {
  const background = isSelectColor(color) ? SELECT_COLORS[color] : UNKNOWN_COLOR
  // The threshold where white text starts winning on the WCAG contrast ratio.
  // Only `deep-dark-green` reaches it today; the rest take near-black.
  return {
    backgroundColor: background,
    color: luminance(background) > 0.35 ? '#1c1c1c' : '#ffffff',
  }
}

/**
 * The next colour to give a new option: the first in the palette nobody is
 * using, or the first of all when every one is taken.
 *
 * Upstream picks at random from the unused ones. A run of options then comes out
 * in an arbitrary order that is different for everybody and different every
 * time, which is neither prettier nor testable; going in palette order gives
 * adjacent options distinct, predictable colours.
 */
export function nextUnusedColor(used: readonly unknown[]): SelectColor {
  const taken = new Set(used)
  return SELECT_COLOR_NAMES.find((name) => !taken.has(name)) ?? SELECT_COLOR_NAMES[0]!
}

/**
 * An id for an option that does not exist yet.
 *
 * Negative and counting down, which is what upstream does and what the backend
 * expects of it: an id it has never issued means "create this", and the *same*
 * number named as the field's default is resolved to the new option once it has
 * one. So a new option can be made the default in the request that creates it.
 */
export function nextOptionId(options: readonly SelectOption[]): number {
  return Math.min(0, ...options.map((option) => option.id ?? 0)) - 1
}

/** Whether an option is one the server has already issued an id for. */
export const isStoredOption = (option: SelectOption): boolean =>
  option.id !== undefined && option.id > 0
